import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardHeader, CardTitle, Input, Select } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/keys.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { EmptyState } from '../../components/Status/EmptyState.js';

interface RateTier {
  id: string;
  from_currency: string;
  to_currency: string;
  min_amount: number;
  max_amount: number | null;
  rate: number;
  is_active: boolean;
}

interface ExchangeRateRow {
  id: string;
  from_currency: string;
  to_currency: string;
  rate: number;
  is_active: boolean;
  effective_from: string;
  effective_to: string | null;
}

interface ManageResp {
  rate_tiers: RateTier[];
  base_rates: ExchangeRateRow[];
}

const SEND = ['EUR', 'USD', 'CAD'] as const;
const RECEIVE = ['XAF'] as const;

export function RatesManagerPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const manageQ = useQuery({
    queryKey: qk.rates,
    queryFn: () => callFunction<ManageResp>('admin-rates-manage'),
  });

  const createBaseRate = useMutation({
    mutationFn: (input: { from_currency: string; to_currency: string; rate: number }) =>
      callFunction('admin-rates-crud', { body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.rates }),
  });

  const disableBaseRate = useMutation({
    mutationFn: (id: string) =>
      callFunction('admin-rates-crud', { method: 'DELETE', query: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.rates }),
  });

  const createTier = useMutation({
    mutationFn: (input: {
      from_currency: string;
      to_currency: string;
      min_amount: number;
      max_amount: number | null;
      rate: number;
    }) => callFunction('admin-rates-manage', { body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.rates }),
  });

  const deleteTier = useMutation({
    mutationFn: (id: string) =>
      callFunction('admin-rates-manage', { method: 'DELETE', query: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.rates }),
  });

  const [baseFrom, setBaseFrom] = useState<(typeof SEND)[number]>('EUR');
  const [baseTo, setBaseTo] = useState<(typeof RECEIVE)[number]>('XAF');
  const [baseRate, setBaseRate] = useState('655.957');

  const [from, setFrom] = useState<(typeof SEND)[number]>('EUR');
  const [minAmount, setMinAmount] = useState('0');
  const [maxAmount, setMaxAmount] = useState('');
  const [tierRate, setTierRate] = useState('655.957');

  if (manageQ.isLoading) return <LoadingScreen />;
  if (manageQ.isError) return <ErrorState onRetry={() => manageQ.refetch()} />;

  const data = manageQ.data!;
  const activeBaseRates = data.base_rates.filter((r) => r.is_active);

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('rates.baseTitle')}</CardTitle>
          <p className="text-xs text-text-muted">{t('rates.baseDescription')}</p>
        </CardHeader>

        <form
          className="mb-4 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const r = Number(baseRate);
            if (!r || r <= 0) return;
            createBaseRate.mutate(
              { from_currency: baseFrom, to_currency: baseTo, rate: r },
              { onSuccess: () => setBaseRate('655.957') },
            );
          }}
        >
          <Select
            value={baseFrom}
            onChange={(v) => setBaseFrom(v as (typeof SEND)[number])}
            options={SEND.map((c) => ({ value: c, label: c }))}
            className="min-w-24"
          />
          <Select
            value={baseTo}
            onChange={(v) => setBaseTo(v as (typeof RECEIVE)[number])}
            options={RECEIVE.map((c) => ({ value: c, label: c }))}
            className="min-w-24"
          />
          <div className="relative min-w-36">
            <Input
              type="number"
              step="0.0001"
              placeholder={t('rates.rate')}
              value={baseRate}
              onChange={(e) => setBaseRate(e.target.value)}
            />
          </div>
          <Button type="submit" isLoading={createBaseRate.isPending}>
            {t('rates.baseSetCta')}
          </Button>
        </form>

        {activeBaseRates.length === 0 ? (
          <EmptyState title={t('rates.baseEmpty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-text-muted">
                  <th className="px-2 py-2">{t('rates.tiersCorridor')}</th>
                  <th className="px-2 py-2">{t('rates.rate')}</th>
                  <th className="px-2 py-2">{t('rates.baseEffectiveFrom')}</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {activeBaseRates.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-2 py-2 font-medium">
                      {r.from_currency} → {r.to_currency}
                    </td>
                    <td className="px-2 py-2 font-mono text-primary">{r.rate}</td>
                    <td className="px-2 py-2 text-xs text-text-muted">
                      {new Date(r.effective_from).toLocaleString()}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => disableBaseRate.mutate(r.id)}
                      >
                        {t('rates.baseDisable')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('rates.tiersTitle')}</CardTitle>
          <p
            className="text-xs text-text-muted"
            dangerouslySetInnerHTML={{ __html: t('rates.tiersDescription') }}
          />
        </CardHeader>

        <form
          className="mb-4 grid grid-cols-2 gap-2 md:grid-cols-6"
          onSubmit={(e) => {
            e.preventDefault();
            const r = Number(tierRate);
            if (!r || r <= 0) return;
            createTier.mutate(
              {
                from_currency: from,
                to_currency: 'XAF',
                min_amount: Number(minAmount),
                max_amount: maxAmount === '' ? null : Number(maxAmount),
                rate: r,
              },
              {
                onSuccess: () => {
                  setMinAmount('0');
                  setMaxAmount('');
                  setTierRate('655.957');
                },
              },
            );
          }}
        >
          <Select
            value={from}
            onChange={(v) => setFrom(v as (typeof SEND)[number])}
            options={SEND.map((c) => ({ value: c, label: `${c} → XAF` }))}
            className="min-w-36"
          />
          <div className="relative">
            <Input
              type="number"
              step="0.01"
              placeholder={t('rates.tiersPlaceholderMin')}
              value={minAmount}
              onChange={(e) => setMinAmount(e.target.value)}
            />
          </div>
          <div className="relative">
            <Input
              type="number"
              step="0.01"
              placeholder={t('rates.tiersPlaceholderMax')}
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
            />
          </div>
          <div className="relative">
            <Input
              type="number"
              step="0.0001"
              placeholder={t('rates.tiersPlaceholderRate')}
              value={tierRate}
              onChange={(e) => setTierRate(e.target.value)}
              className="pr-14"
            />
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-text-muted">
              XAF
            </span>
          </div>
          <Button type="submit" isLoading={createTier.isPending}>
            {t('common.save')}
          </Button>
        </form>

        {data.rate_tiers.length === 0 ? (
          <EmptyState title={t('rates.tiersEmpty')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-text-muted">
                  <th className="px-2 py-2">{t('rates.tiersCorridor')}</th>
                  <th className="px-2 py-2">{t('rates.tiersPlaceholderMin')}</th>
                  <th className="px-2 py-2">{t('rates.tiersPlaceholderMax')}</th>
                  <th className="px-2 py-2">{t('rates.rate')}</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.rate_tiers.map((tier) => (
                  <tr key={tier.id} className="border-t border-border">
                    <td className="px-2 py-2">
                      {tier.from_currency}→{tier.to_currency}
                    </td>
                    <td className="px-2 py-2 font-mono">{tier.min_amount}</td>
                    <td className="px-2 py-2 font-mono">{tier.max_amount ?? '∞'}</td>
                    <td className="px-2 py-2 font-mono">{tier.rate}</td>
                    <td className="px-2 py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => deleteTier.mutate(tier.id)}>
                        {t('common.delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
